import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  Menu,
  MenuItem,
  Typography,
  ListItemIcon,
  useMediaQuery,
  useTheme,
  Box,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import { toast } from 'react-toastify';
import { UserContext } from '../context/UserContext';
import { USER_ROLES } from '../constants';
import MobileLoginBlock from './MobileLoginBlock';
import CustomLink from './CustomLink';
import { requestNavigation } from '../utils/navigationGuard';
import { requestUnderstandLeave } from '../utils/understandLeave';
import CompanyDropdown from './CompanyDropdown';
import LegalDropdown from './LegalDropdown';

const Navbar = () => {
  const { user, logout } = useContext(UserContext);
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isNotApproved = user?.role === USER_ROLES.LEAD_AGENT && !user?.isApproved;
  const isSettingsDisabled = user?.role === USER_ROLES.CLIENT || user?.role === USER_ROLES.ADMIN;

  const [anchorEl, setAnchorEl] = useState(null);
  const [showMobileBlock, setShowMobileBlock] = useState(false);

  const handleMouseEnter = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
  };

  const handleLogout = () =>
    requestUnderstandLeave(async () => {
      try {
        await logout();
        setAnchorEl(null);
        toast('Logged out successfully!', { type: 'success' });
      } catch (error) {
        toast('Failed to logout', { type: 'error' });
      }
    });

  const handleLoginClick = (e) => {
    if (isMobileOrTablet) {
      e.preventDefault();
      setShowMobileBlock(true);
    }
  };

  const getRoleLabel = () => {
    if (user?.role === USER_ROLES.LEAD_AGENT) return ' (LA)';
    if (user?.role === USER_ROLES.CLIENT) return ' (Client)';
    return '';
  };

  const handleLogoClick = async () => {
    if (!requestNavigation('/')) return;
    if (isNotApproved) {
      await handleLogout();
    }
    navigate('/');
  };

  return (
    <>
      <AppBar style={{ backgroundColor: 'var(--secondary-color)', position: 'relative' }}>
        <Toolbar
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            variant='h6'
            style={{
              color: 'var(--primary-color)',
              textDecoration: 'none',
              fontWeight: 'bold',
              fontSize: '1.5rem',
              cursor: 'pointer',
              transition: 'color 0.3s ease',
            }}
            onMouseEnter={(e) => (e.target.style.color = 'var(--accent-color)')}
            onMouseLeave={(e) => (e.target.style.color = 'var(--primary-color)')}
            onClick={handleLogoClick}
          >
            TechXpander
          </Typography>

          {user ? (
            <>
              <div
                role='button'
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
                onMouseEnter={handleMouseEnter}
              >
                <Typography
                  style={{
                    color: 'white',
                    textTransform: 'none',
                    marginRight: '4px',
                  }}
                >
                  {user.firstName || 'User'}
                  {getRoleLabel()}
                </Typography>
                <ArrowDropDownIcon style={{ color: 'white' }} />
              </div>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMouseLeave}
                MenuListProps={{
                  onMouseEnter: () => setAnchorEl(anchorEl),
                  onMouseLeave: handleMouseLeave,
                }}
              >
                <MenuItem disabled>
                  <Typography variant='body2' color='textSecondary'>
                    {user.email || 'User Email'}
                  </Typography>
                </MenuItem>

                <MenuItem
                  component={CustomLink}
                  to='/settings'
                  onClick={handleMouseLeave}
                  disabled={isSettingsDisabled}
                >
                  <ListItemIcon>
                    <SettingsIcon fontSize='small' />
                  </ListItemIcon>
                  <Typography variant='body2'>Settings</Typography>
                </MenuItem>

                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize='small' />
                  </ListItemIcon>
                  <Typography variant='body2'>Sign Out</Typography>
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Box display='flex' gap={4}>
              <Box sx={{ pr: 4, gap: 4, borderRight: '1px solid white', display: { xs: 'none', sm: 'flex' } }}>
                <CompanyDropdown />
                <LegalDropdown />
              </Box>
              <Button
                color='inherit'
                component={CustomLink}
                to='/login'
                sx={{ textTransform: 'none' }}
                onClick={handleLoginClick}
              >
                Sign In
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <MobileLoginBlock open={showMobileBlock} onClose={() => setShowMobileBlock(false)} />
    </>
  );
};

export default Navbar;
